import type { IComparisonSection } from '~/components/ComparisonTable.vue';
import { TOTAL_INTEGRATIONS, TOTAL_UNIFIED_APIS } from './integrations';
import { PLANS, type IPlan } from '@/models/UnifiedInternal';

export const ACTIVE_PLANS = PLANS.filter((p) => p.is_active);

const getPlanValue = <K extends keyof IPlan>(planName: string, field: K): IPlan[K] | undefined => {
    return ACTIVE_PLANS.find((p) => p.name === planName)?.[field];
};

const formatNumber = (value: number | undefined): string => {
    return value?.toLocaleString() ?? '0';
};

const formatPrice = (value: number | undefined): string => {
    return value ? `$${value.toFixed(2)}` : '0';
};

const getConnectionLimit = (planName: string): string => {
    const maxConnections = getPlanValue(planName, 'max_connections');
    return maxConnections === 0 ? 'Unlimited' : formatNumber(maxConnections);
};

const getRequestLimit = (planName: string): string => {
    const maxRequests = getPlanValue(planName, 'max_requests');
    return maxRequests === 0 ? 'Unlimited' : formatNumber(maxRequests);
};

const MAX_CONNECTIONS_START = 100;

export enum PlanName {
    Test = 'Test',
    Start = 'Start',
    Grow = 'Grow',
    Scale = 'Scale',
    Pro = 'Pro',
}

export interface PlanFeatures {
    subtitle: string;
    features: string[];
}

export const PLAN_FEATURES_SUMMARY: Record<PlanName, PlanFeatures> = {
    [PlanName.Test]: {
        subtitle: 'Try us out for free with:',
        features: [
            'Unlimited API Calls',
            'Unlimited Customers',
            'Unlimited Connections',
            `${TOTAL_UNIFIED_APIS}+ Unified APIs`,
            `${TOTAL_INTEGRATIONS}+ Integrations`,
            `${getPlanValue('Test', 'dedicated_channel') ? 'Private Slack or Discord' : 'Community Discord'}`,
        ],
    },
    [PlanName.Start]: {
        subtitle: 'After your 30-day free trial, launch with:',
        features: [
            `${getRequestLimit('Start')} Monthly API Calls`,
            `${formatPrice(getPlanValue('Start', 'price_per_mille_requests'))}/1000 API Call Overages`,
            `Up to ${getConnectionLimit('Start')} Customers`,
            `${getConnectionLimit('Start')} Connections`,
            `${TOTAL_UNIFIED_APIS}+ Unified APIs`,
            `${TOTAL_INTEGRATIONS}+ Integrations`,
            `${getPlanValue('Start', 'dedicated_channel') ? 'Private Slack or Discord' : 'Community Discord'}`,
        ],
    },
    [PlanName.Grow]: {
        subtitle: 'After your 30-day free trial, grow with:',
        features: [
            `${getRequestLimit('Grow')} Monthly API Calls`,
            `${formatPrice(getPlanValue('Grow', 'price_per_mille_requests'))}/1000 API Call Overages`,
            'Unlimited Customers',
            'Unlimited Connections',
            'Zero setup fees & no hidden costs',
            '2 Monthly Support Hours',
            `${TOTAL_UNIFIED_APIS}+ Unified APIs`,
            `${TOTAL_INTEGRATIONS}+ Integrations`,
            'Virtual Webhooks & Database Sync',
            `${getPlanValue('Grow', 'dedicated_channel') ? 'Private Slack or Discord' : 'Community Discord'}`,
            `${getPlanValue('Grow', 'custom_oauth') ? 'API CNAME' : ''}`,
        ],
    },
    [PlanName.Pro]: {
        subtitle: 'For secure production applications with high customer usage.',
        features: [
            `${getRequestLimit('Pro')} Monthly API Calls`,
            `${formatPrice(getPlanValue('Pro', 'price_per_mille_requests'))}/1000 API Call Overages`,
            // 'Unlimited Customers',
            // 'Unlimited Connections',
            // `${TOTAL_UNIFIED_APIS}+ Unified APIs`,
            // `${TOTAL_INTEGRATIONS}+ Integrations`,
            // 'Virtual Webhooks & Database Sync',
            // `${getPlanValue('Pro', 'dedicated_channel') ? 'Private Slack or Discord' : 'Community Discord'}`,
            'Integration Request Priority',
            '5 Monthly Support Hours',
            // `${getPlanValue('Pro', 'custom_oauth') ? 'API CNAME' : ''}`,
            `${getPlanValue('Pro', 'custom_oauth') ? 'Auth CNAME' : ''}`,
            `${getPlanValue('Pro', 'custom_oauth') ? 'Secrets Manager key storage' : ''}`,
            // '90-day log retention',
            'SAML SSO',
        ],
    },
    [PlanName.Scale]: {
        subtitle: 'Everything from Grow Plan, plus:',
        features: [
            `${getRequestLimit('Scale')} Monthly API Calls`,
            `${formatPrice(getPlanValue('Scale', 'price_per_mille_requests'))}/1000 API Call Overages`,
            // 'Unlimited Customers',
            // 'Unlimited Connections',
            // `${TOTAL_UNIFIED_APIS}+ Unified APIs`,
            // `${TOTAL_INTEGRATIONS}+ Integrations`,
            // 'Virtual Webhooks & Database Sync',
            // `${getPlanValue('Scale', 'dedicated_channel') ? 'Private Slack or Discord' : 'Community Discord'}`,
            'Integration Request Priority',
            // `${getPlanValue('Scale', 'custom_oauth') ? 'API CNAME' : ''}`,
            `${getPlanValue('Scale', 'custom_oauth') ? 'Auth CNAME' : ''}`,
            `${getPlanValue('Scale', 'custom_oauth') ? 'Secrets Manager key storage' : ''}`,
            '365-day log retention',
            'Premium Support',
            '15 Monthly Support Hours',
            'SAML SSO',
            'BAA for HIPAA Compliance',
        ],
    },
} as const;

export const PLAN_FEATURES_EXPANDED: IComparisonSection[] = [
    {
        title: 'Unified API & MCP',
        rows: [
            {
                feature: 'Customer Connections',
                tooltip: 'Authenticated integrations between your customer and their third-party account',
                text: {
                    [PlanName.Test]: `${getConnectionLimit('Test')}`,
                    // [PlanName.Start]: `${getConnectionLimit('Start')}`,
                    [PlanName.Grow]: `${getConnectionLimit('Grow')}`,
                    [PlanName.Pro]: `${getConnectionLimit('Pro')}`,
                    [PlanName.Scale]: `${getConnectionLimit('Scale')}`,
                },
            },
            {
                feature: 'API Calls per Month',
                tooltip: 'API calls included in your plan',
                text: {
                    [PlanName.Test]: `${getRequestLimit('Test')}`,
                    // [PlanName.Start]: `${getRequestLimit('Start')}`,
                    [PlanName.Grow]: `${getRequestLimit('Grow')}`,
                    [PlanName.Pro]: `${getRequestLimit('Pro')}`,
                    [PlanName.Scale]: `${getRequestLimit('Scale')}`,
                },
            },
            {
                feature: 'API Call Overages per Month',
                text: {
                    [PlanName.Test]: 'N/A',
                    // [PlanName.Start]: `${formatPrice(getPlanValue('Start', 'price_per_mille_requests'))}/1000`,
                    [PlanName.Pro]: `${formatPrice(getPlanValue('Pro', 'price_per_mille_requests'))}/1000`,
                    [PlanName.Grow]: `${formatPrice(getPlanValue('Grow', 'price_per_mille_requests'))}/1000`,
                    [PlanName.Scale]: `${formatPrice(getPlanValue('Scale', 'price_per_mille_requests'))}/1000`,
                },
            },
            {
                feature: 'Rate Limits',
                tooltip: 'API calls per minute',
                text: {
                    [PlanName.Test]: `${getPlanValue('Test', 'rate_limit')}`,
                    // [PlanName.Start]: `${getPlanValue('Start', 'rate_limit')}`,
                    [PlanName.Pro]: `${getPlanValue('Pro', 'rate_limit')}`,
                    [PlanName.Grow]: `${getPlanValue('Grow', 'rate_limit')}`,
                    [PlanName.Scale]: `${getPlanValue('Scale', 'rate_limit')}`,
                },
            },
            {
                feature: 'File Download Limits',
                tooltip: 'Concurrent downloads',
                text: {
                    [PlanName.Test]: '10',
                    // [PlanName.Start]: '25',
                    [PlanName.Pro]: '75',
                    [PlanName.Grow]: '50',
                    [PlanName.Scale]: '100',
                },
            },
            {
                feature: 'API Data Transfer',
                text: {
                    [PlanName.Test]: 'Unlimited',
                    // [PlanName.Start]: 'Unlimited',
                    [PlanName.Pro]: 'Unlimited',
                    [PlanName.Grow]: 'Unlimited',
                    [PlanName.Scale]: 'Unlimited',
                },
            },
            {
                feature: 'Customers',
                text: {
                    [PlanName.Test]: `${getConnectionLimit('Test')}`,
                    // [PlanName.Start]: `Up to ${getConnectionLimit('Start')}`,
                    [PlanName.Pro]: 'Unlimited',
                    [PlanName.Grow]: `${getConnectionLimit('Grow')}`,
                    [PlanName.Scale]: `${getConnectionLimit('Scale')}`,
                },
            },
            {
                feature: 'Unified APIs',
                text: {
                    [PlanName.Test]: `${TOTAL_UNIFIED_APIS}+`,
                    // [PlanName.Start]: `${TOTAL_UNIFIED_APIS}+`,
                    [PlanName.Pro]: `${TOTAL_UNIFIED_APIS}+`,
                    [PlanName.Grow]: `${TOTAL_UNIFIED_APIS}+`,
                    [PlanName.Scale]: `${TOTAL_UNIFIED_APIS}+`,
                },
            },
            {
                feature: 'Pre-built Integrations',
                text: {
                    [PlanName.Test]: `${TOTAL_INTEGRATIONS}+`,
                    // [PlanName.Start]: `${TOTAL_INTEGRATIONS}+`,
                    [PlanName.Pro]: `${TOTAL_INTEGRATIONS}+`,
                    [PlanName.Grow]: `${TOTAL_INTEGRATIONS}+`,
                    [PlanName.Scale]: `${TOTAL_INTEGRATIONS}+`,
                },
            },
            {
                feature: 'Webhooks (Native)',
                tooltip: 'Receive Real-time updates via Native push',
                text: {
                    [PlanName.Test]: 'Unlimited',
                    // [PlanName.Start]: 'Unlimited',
                    [PlanName.Pro]: 'Unlimited',
                    [PlanName.Grow]: 'Unlimited',
                    [PlanName.Scale]: 'Unlimited',
                },
            },
            {
                feature: 'Webhooks (Virtual)',
                tooltip: 'Receive Real-time updates via Virtual polling webhooks',
                text: {
                    [PlanName.Test]: 'Unlimited',
                    // [PlanName.Start]: '-',
                    [PlanName.Pro]: 'Unlimited',
                    [PlanName.Grow]: 'Unlimited',
                    [PlanName.Scale]: 'Unlimited',
                },
            },
            {
                feature: 'Database Sync',
                tooltip: 'Receive Real-time updates directly into your database',
                text: {
                    [PlanName.Test]: 'Unlimited',
                    // [PlanName.Start]: '-',
                    [PlanName.Pro]: 'Unlimited',
                    [PlanName.Grow]: 'Unlimited',
                    [PlanName.Scale]: 'Unlimited',
                },
            },
            {
                feature: 'Custom & Original Fields',
                tooltip: 'Use original fields from integrations or add custom fields',
                text: {
                    [PlanName.Test]: 'Yes',
                    // [PlanName.Start]: 'Yes',
                    [PlanName.Pro]: 'Yes',
                    [PlanName.Grow]: 'Yes',
                    [PlanName.Scale]: 'Yes',
                },
            },
            {
                feature: 'Passthrough API',
                tooltip: "Use your customer's connection to access unsupported API endpoints",
                text: {
                    [PlanName.Test]: 'Yes',
                    // [PlanName.Start]: 'Yes',
                    [PlanName.Pro]: 'Yes',
                    [PlanName.Grow]: 'Yes',
                    [PlanName.Scale]: 'Yes',
                },
            },
            {
                feature: 'Branding',
                text: {
                    [PlanName.Test]: '-',
                    // [PlanName.Start]: 'Yes',
                    [PlanName.Pro]: 'Yes',
                    [PlanName.Grow]: 'Yes',
                    [PlanName.Scale]: 'Yes',
                },
            },
            {
                feature: 'API CNAME',
                tooltip: 'Replace api.unified.to with your own domain',
                text: {
                    [PlanName.Test]: '-',
                    // [PlanName.Start]: '-',
                    [PlanName.Pro]: 'Yes',
                    [PlanName.Grow]: 'Yes',
                    [PlanName.Scale]: 'Yes',
                },
            },

            {
                feature: 'Log Retention',
                text: {
                    [PlanName.Test]: '30-days',
                    // [PlanName.Start]: '60-days',
                    [PlanName.Grow]: '60-days',
                    [PlanName.Pro]: '60-days',
                    [PlanName.Scale]: '365+ days',
                },
            },
            // {
            //     feature: 'Datadog Logs',
            //     text: {
            //         [PlanName.Test]: '-',
            //         // [PlanName.Start]: '-',
            //         [PlanName.Grow]: '-',
            //         [PlanName.Pro]: '-',
            //         [PlanName.Scale]: 'Yes',
            //     },
            // },
            {
                feature: 'Datadog Logs',
                tooltip: 'Generate and copy your Datadog API key in the Datadog Organization Settings page',
                text: {
                    [PlanName.Test]: '-',
                    // [PlanName.Start]: '-',
                    [PlanName.Grow]: '-',
                    [PlanName.Pro]: 'Yes',
                    [PlanName.Scale]: 'Yes',
                },
            },
            {
                feature: 'Production Capable',
                text: {
                    [PlanName.Test]: '-',
                    // [PlanName.Start]: 'Yes',
                    [PlanName.Grow]: 'Yes',
                    [PlanName.Pro]: 'Yes',
                    [PlanName.Scale]: 'Yes',
                },
            },
        ],
    },
    {
        title: 'Security',
        rows: [
            {
                feature: 'Secrets Manager key storage',
                tooltip:
                    "Use your AWS Secrets Manager, Azure Key Vault, HashiCorp Vault, or Google Cloud Secrets Manager or other secrets manager account to store your customers' API credentials/Oauth2 tokens",
                text: {
                    [PlanName.Test]: '-',
                    // [PlanName.Start]: '-',
                    [PlanName.Grow]: '-',
                    [PlanName.Pro]: 'Yes',
                    [PlanName.Scale]: 'Yes',
                },
            },
            {
                feature: 'SAML SSO',
                tooltip: "Single Sign-On into app.unified.to with your organization's SAML identity provider",
                text: {
                    [PlanName.Test]: '-',
                    // [PlanName.Start]: '-',
                    [PlanName.Grow]: '-',
                    [PlanName.Pro]: 'Yes',
                    [PlanName.Scale]: 'Yes',
                },
            },
            {
                feature: 'Restricted IP Addresses',
                tooltip: "Restrict your server's IP addresses when calling the Unified.to API",
                text: {
                    [PlanName.Test]: '-',
                    [PlanName.Start]: '-',
                    [PlanName.Grow]: '-',
                    [PlanName.Pro]: 'Yes',
                    [PlanName.Scale]: 'Yes',
                },
            },
            {
                feature: 'Auth CNAME',
                tooltip: 'Replace auth.unified.to with your own domain',
                text: {
                    [PlanName.Test]: '-',
                    // [PlanName.Start]: '-',
                    [PlanName.Grow]: '-',
                    [PlanName.Pro]: 'Yes',
                    [PlanName.Scale]: 'Yes',
                },
            },

            {
                feature: 'Data region sync',
                tooltip: 'Syncronize workspace management configuration to all global data-centers',
                text: {
                    [PlanName.Test]: '-',
                    [PlanName.Start]: '-',
                    [PlanName.Grow]: '-',
                    [PlanName.Pro]: '-',
                    [PlanName.Scale]: 'Yes',
                },
            },
        ],
    },
    {
        title: 'Developer Tools',
        rows: [
            {
                feature: 'Integrations Explorer',
                text: {
                    [PlanName.Test]: 'Yes',
                    // [PlanName.Start]: 'Yes',
                    [PlanName.Grow]: 'Yes',
                    [PlanName.Pro]: 'Yes',
                    [PlanName.Scale]: 'Yes',
                },
            },
            {
                feature: 'SDKs',
                text: {
                    [PlanName.Test]: 'Typescript, Python, PHP, Java, C#, Ruby, Go',
                    [PlanName.Start]: 'Typescript, Python, PHP, Java, C#, Ruby, Go',
                    [PlanName.Grow]: 'Typescript, Python, PHP, Java, C#, Ruby, Go',
                    [PlanName.Pro]: 'Typescript, Python, PHP, Java, C#, Ruby, Go',
                    [PlanName.Scale]: 'Typescript, Python, PHP, Java, C#, Ruby, Go',
                },
            },
            {
                feature: 'Sandbox and Dev Environments',
                text: {
                    [PlanName.Test]: 'Unlimited',
                    // [PlanName.Start]: 'Unlimited',
                    [PlanName.Grow]: 'Unlimited',
                    [PlanName.Pro]: 'Unlimited',
                    [PlanName.Scale]: 'Unlimited',
                },
            },
            {
                feature: 'Embedded Authorization',
                tooltip: 'An in-app component for customer authorization',
                text: {
                    [PlanName.Test]: 'React, VueJS, Angular, JS',
                    // [PlanName.Start]: 'React, VueJS, Angular, JS',
                    [PlanName.Grow]: 'React, VueJS, Angular, JS',
                    [PlanName.Pro]: 'React, VueJS, Angular, JS',
                    [PlanName.Scale]: 'React, VueJS, Angular, JS',
                },
            },
            {
                feature: 'Embedded Single Sign-on (SSO)',
                tooltip: 'An in-app single sign-on component for customer authentication',
                text: {
                    [PlanName.Test]: 'JS + CSS',
                    // [PlanName.Start]: 'JS + CSS',
                    [PlanName.Grow]: 'JS + CSS',
                    [PlanName.Pro]: 'JS + CSS',
                    [PlanName.Scale]: 'JS + CSS',
                },
            },
            {
                feature: 'Embedded Directory',
                tooltip: 'Add an integration directory to your website for browsing',
                text: {
                    [PlanName.Test]: 'JS + CSS',
                    // [PlanName.Start]: 'JS + CSS',
                    [PlanName.Grow]: 'JS + CSS',
                    [PlanName.Pro]: 'JS + CSS',
                    [PlanName.Scale]: 'JS + CSS',
                },
            },
        ],
    },
    {
        title: 'Support',
        rows: [
            {
                feature: 'Support Channel',
                text: {
                    [PlanName.Test]: `${
                        PLANS.find((p) => p.name === 'Test' && p.is_active)?.dedicated_channel ? 'Private Slack/Discord' : 'Community Discord'
                    }`,
                    [PlanName.Start]: `${
                        PLANS.find((p) => p.name === 'Start' && p.is_active)?.dedicated_channel ? 'Private Slack/Discord' : 'Community Discord'
                    }`,
                    [PlanName.Grow]: `${
                        PLANS.find((p) => p.name === 'Grow' && p.is_active)?.dedicated_channel ? 'Private Slack/Discord' : 'Community Discord'
                    }`,
                    [PlanName.Pro]: `${PLANS.find((p) => p.name === 'Pro' && p.is_active)?.dedicated_channel ? 'Private Slack/Discord' : 'Community Discord'}`,
                    [PlanName.Scale]: `${
                        PLANS.find((p) => p.name === 'Scale' && p.is_active)?.dedicated_channel ? 'Private Slack/Discord/Email' : 'Community Discord'
                    }`,
                },
            },
            {
                feature: 'Monthly Support Hours',
                tooltip: '"support hours" do not include time spent with support/product team on new feature, backfill, and defects requests.',
                text: {
                    [PlanName.Test]: '2',
                    [PlanName.Start]: '2',
                    [PlanName.Grow]: '2',
                    [PlanName.Pro]: '5',
                    [PlanName.Scale]: '15',
                },
            },
            {
                feature: 'Response Time (Business Hours)',
                tooltip: 'Response times are prioritized based on plan level',
                text: {
                    [PlanName.Test]: '<24 hr',
                    [PlanName.Start]: '<12 hr',
                    [PlanName.Grow]: '<3 hr',
                    [PlanName.Pro]: '<3 hr',
                    [PlanName.Scale]: '<1 hr',
                },
            },
            {
                feature: 'Response Time (Non-Business Hours)',
                tooltip: 'Available for critical system issues or customers on the Scale plan',
                text: {
                    [PlanName.Test]: '-',
                    [PlanName.Start]: '-',
                    [PlanName.Grow]: '-',
                    [PlanName.Pro]: '-',
                    [PlanName.Scale]: '<12 hr',
                },
            },
            {
                feature: 'Discovery/Onboarding Calls',
                tooltip: 'Business and technical calls before you upgrade',
                text: {
                    [PlanName.Test]: 'Yes',
                    [PlanName.Start]: 'Yes',
                    [PlanName.Grow]: 'Yes',
                    [PlanName.Pro]: 'Yes',
                    [PlanName.Scale]: 'Yes',
                },
            },
            {
                feature: 'Hours for non-standard calls',
                tooltip: 'Additional available hours for calls outside of discovery, onboarding, and monthly reviews',
                text: {
                    [PlanName.Test]: '-',
                    [PlanName.Start]: '-',
                    [PlanName.Grow]: '-',
                    [PlanName.Pro]: '9am-5pm ET',
                    [PlanName.Scale]: '8am-7pm ET',
                },
            },
            {
                feature: 'New Integration Requests',
                text: {
                    [PlanName.Test]: 'Added to Roadmap',
                    [PlanName.Start]: 'Added to Roadmap',
                    [PlanName.Grow]: 'Added to Roadmap',
                    [PlanName.Pro]: 'Prioritized',
                    [PlanName.Scale]: 'Prioritized',
                },
            },
            {
                feature: 'Monthly Post-Launch Account Review',
                text: {
                    [PlanName.Test]: '-',
                    [PlanName.Start]: '-',
                    [PlanName.Grow]: '-',
                    [PlanName.Pro]: '-',
                    [PlanName.Scale]: 'Yes',
                },
            },
            {
                feature: 'Pre-Launch Weekly Project Meetings',
                text: {
                    [PlanName.Test]: '-',
                    [PlanName.Start]: '-',
                    [PlanName.Grow]: '-',
                    [PlanName.Pro]: 'Yes',
                    [PlanName.Scale]: 'Yes',
                },
            },
            {
                feature: 'Set-up Fees',
                tooltip: `We don't charge fees for set-up support`,
                text: {
                    [PlanName.Test]: '-',
                    [PlanName.Start]: '-',
                    [PlanName.Grow]: '-',
                    [PlanName.Pro]: '-',
                    [PlanName.Scale]: '-',
                },
            },
        ],
    },
] as const;
